/**
 * RFI (Request for Information) API Service - RTK Query
 * BuilderBel Construction Project Management System
 */

import { baseApi } from './api';
import { ApiResponse } from '../types';
import type {
  RFI,
  RFIListParams,
  RFIListResponse,
  RFIStats,
  CreateRFIRequest,
  UpdateRFIRequest,
  SubmitRFIRequest,
  AssignRFIRequest,
  RespondToRFIRequest,
  CloseRFIRequest,
  AddRFICommentRequest,
  UpdateRFICommentRequest,
  AddRFIDistributionRequest,
  AddRFIReferenceRequest,
  AddRFIAttachmentRequest,
  BulkAssignRFIsRequest,
  BulkCloseRFIsRequest,
} from '../types/rfi';

/**
 * RFI API Endpoints
 */
export const rfiApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    // ============================================================================
    // RFI CRUD
    // ============================================================================

    // Get all RFIs for a project
    getRFIs: builder.query<
      ApiResponse<RFIListResponse>,
      { projectId: string; params?: RFIListParams }
    >({
      query: ({ projectId, params }) => ({
        url: `/projects/${projectId}/rfis`,
        params,
      }),
      providesTags: ['RFI'],
    }),

    // Get single RFI
    getRFI: builder.query<ApiResponse<RFI>, { projectId: string; rfiId: string }>({
      query: ({ projectId, rfiId }) => ({
        url: `/projects/${projectId}/rfis/${rfiId}`,
      }),
      providesTags: (_result, _error, { rfiId }) => [{ type: 'RFI', id: rfiId }],
    }),

    // Create RFI
    createRFI: builder.mutation<ApiResponse<RFI>, { projectId: string; data: CreateRFIRequest }>({
      query: ({ projectId, data }) => ({
        url: `/projects/${projectId}/rfis`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['RFI'],
    }),

    // Update RFI
    updateRFI: builder.mutation<
      ApiResponse<RFI>,
      { projectId: string; rfiId: string; data: UpdateRFIRequest }
    >({
      query: ({ projectId, rfiId, data }) => ({
        url: `/projects/${projectId}/rfis/${rfiId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { rfiId }) => ['RFI', { type: 'RFI', id: rfiId }],
    }),

    // Delete RFI (soft delete)
    deleteRFI: builder.mutation<ApiResponse<void>, { projectId: string; rfiId: string }>({
      query: ({ projectId, rfiId }) => ({
        url: `/projects/${projectId}/rfis/${rfiId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['RFI'],
    }),

    // ============================================================================
    // WORKFLOW OPERATIONS
    // ============================================================================

    // Submit RFI (draft -> open)
    submitRFI: builder.mutation<
      ApiResponse<RFI>,
      { projectId: string; rfiId: string; data?: SubmitRFIRequest }
    >({
      query: ({ projectId, rfiId, data }) => ({
        url: `/projects/${projectId}/rfis/${rfiId}/submit`,
        method: 'POST',
        body: data || {},
      }),
      invalidatesTags: (_result, _error, { rfiId }) => ['RFI', { type: 'RFI', id: rfiId }],
    }),

    // Assign RFI (change ball-in-court)
    assignRFI: builder.mutation<
      ApiResponse<RFI>,
      { projectId: string; rfiId: string; data: AssignRFIRequest }
    >({
      query: ({ projectId, rfiId, data }) => ({
        url: `/projects/${projectId}/rfis/${rfiId}/assign`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { rfiId }) => ['RFI', { type: 'RFI', id: rfiId }],
    }),

    // Respond to RFI (official response)
    respondToRFI: builder.mutation<
      ApiResponse<RFI>,
      { projectId: string; rfiId: string; data: RespondToRFIRequest }
    >({
      query: ({ projectId, rfiId, data }) => ({
        url: `/projects/${projectId}/rfis/${rfiId}/respond`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { rfiId }) => ['RFI', { type: 'RFI', id: rfiId }],
    }),

    // Close RFI
    closeRFI: builder.mutation<
      ApiResponse<RFI>,
      { projectId: string; rfiId: string; data?: CloseRFIRequest }
    >({
      query: ({ projectId, rfiId, data }) => ({
        url: `/projects/${projectId}/rfis/${rfiId}/close`,
        method: 'POST',
        body: data || {},
      }),
      invalidatesTags: (_result, _error, { rfiId }) => ['RFI', { type: 'RFI', id: rfiId }],
    }),

    // Void RFI
    voidRFI: builder.mutation<
      ApiResponse<RFI>,
      { projectId: string; rfiId: string; data?: CloseRFIRequest }
    >({
      query: ({ projectId, rfiId, data }) => ({
        url: `/projects/${projectId}/rfis/${rfiId}/void`,
        method: 'POST',
        body: data || {},
      }),
      invalidatesTags: (_result, _error, { rfiId }) => ['RFI', { type: 'RFI', id: rfiId }],
    }),

    // ============================================================================
    // COMMENT OPERATIONS
    // ============================================================================

    // Add comment
    addRFIComment: builder.mutation<
      ApiResponse<RFI>,
      { projectId: string; rfiId: string; data: AddRFICommentRequest }
    >({
      query: ({ projectId, rfiId, data }) => ({
        url: `/projects/${projectId}/rfis/${rfiId}/comments`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { rfiId }) => ['RFI', { type: 'RFI', id: rfiId }],
    }),

    // Update comment
    updateRFIComment: builder.mutation<
      ApiResponse<RFI>,
      { projectId: string; rfiId: string; commentId: string; data: UpdateRFICommentRequest }
    >({
      query: ({ projectId, rfiId, commentId, data }) => ({
        url: `/projects/${projectId}/rfis/${rfiId}/comments/${commentId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { rfiId }) => ['RFI', { type: 'RFI', id: rfiId }],
    }),

    // Delete comment
    deleteRFIComment: builder.mutation<
      ApiResponse<RFI>,
      { projectId: string; rfiId: string; commentId: string }
    >({
      query: ({ projectId, rfiId, commentId }) => ({
        url: `/projects/${projectId}/rfis/${rfiId}/comments/${commentId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { rfiId }) => ['RFI', { type: 'RFI', id: rfiId }],
    }),

    // ============================================================================
    // DISTRIBUTION LIST OPERATIONS
    // ============================================================================

    // Add to distribution list
    addRFIDistribution: builder.mutation<
      ApiResponse<RFI>,
      { projectId: string; rfiId: string; data: AddRFIDistributionRequest }
    >({
      query: ({ projectId, rfiId, data }) => ({
        url: `/projects/${projectId}/rfis/${rfiId}/distribution`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { rfiId }) => ['RFI', { type: 'RFI', id: rfiId }],
    }),

    // Remove from distribution list
    removeRFIDistribution: builder.mutation<
      ApiResponse<RFI>,
      { projectId: string; rfiId: string; distributionId: string }
    >({
      query: ({ projectId, rfiId, distributionId }) => ({
        url: `/projects/${projectId}/rfis/${rfiId}/distribution/${distributionId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { rfiId }) => ['RFI', { type: 'RFI', id: rfiId }],
    }),

    // ============================================================================
    // REFERENCE OPERATIONS
    // ============================================================================

    // Add reference
    addRFIReference: builder.mutation<
      ApiResponse<RFI>,
      { projectId: string; rfiId: string; data: AddRFIReferenceRequest }
    >({
      query: ({ projectId, rfiId, data }) => ({
        url: `/projects/${projectId}/rfis/${rfiId}/references`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { rfiId }) => ['RFI', { type: 'RFI', id: rfiId }],
    }),

    // Remove reference
    removeRFIReference: builder.mutation<
      ApiResponse<RFI>,
      { projectId: string; rfiId: string; referenceId: string }
    >({
      query: ({ projectId, rfiId, referenceId }) => ({
        url: `/projects/${projectId}/rfis/${rfiId}/references/${referenceId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { rfiId }) => ['RFI', { type: 'RFI', id: rfiId }],
    }),

    // ============================================================================
    // ATTACHMENT OPERATIONS
    // ============================================================================

    // Add attachment
    addRFIAttachment: builder.mutation<
      ApiResponse<RFI>,
      { projectId: string; rfiId: string; data: AddRFIAttachmentRequest }
    >({
      query: ({ projectId, rfiId, data }) => ({
        url: `/projects/${projectId}/rfis/${rfiId}/attachments`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { rfiId }) => ['RFI', { type: 'RFI', id: rfiId }],
    }),

    // Remove attachment
    removeRFIAttachment: builder.mutation<
      ApiResponse<RFI>,
      { projectId: string; rfiId: string; attachmentId: string }
    >({
      query: ({ projectId, rfiId, attachmentId }) => ({
        url: `/projects/${projectId}/rfis/${rfiId}/attachments/${attachmentId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { rfiId }) => ['RFI', { type: 'RFI', id: rfiId }],
    }),

    // ============================================================================
    // STATISTICS & SPECIAL OPERATIONS
    // ============================================================================

    // Get RFI statistics
    getRFIStats: builder.query<ApiResponse<RFIStats>, { projectId: string }>({
      query: ({ projectId }) => ({
        url: `/projects/${projectId}/rfis/stats`,
      }),
      providesTags: ['RFI'],
    }),

    // Get RFIs assigned to current user
    getMyRFIs: builder.query<ApiResponse<RFI[]>, { projectId: string }>({
      query: ({ projectId }) => ({
        url: `/projects/${projectId}/rfis/my-rfis`,
      }),
      providesTags: ['RFI'],
    }),

    // Bulk assign RFIs
    bulkAssignRFIs: builder.mutation<
      ApiResponse<{ modifiedCount: number }>,
      { projectId: string; data: BulkAssignRFIsRequest }
    >({
      query: ({ projectId, data }) => ({
        url: `/projects/${projectId}/rfis/bulk-assign`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['RFI'],
    }),

    // Bulk close RFIs
    bulkCloseRFIs: builder.mutation<
      ApiResponse<{ modifiedCount: number }>,
      { projectId: string; data: BulkCloseRFIsRequest }
    >({
      query: ({ projectId, data }) => ({
        url: `/projects/${projectId}/rfis/bulk-close`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['RFI'],
    }),
  }),
});

/**
 * Export hooks for use in components
 */
export const {
  // CRUD
  useGetRFIsQuery,
  useGetRFIQuery,
  useCreateRFIMutation,
  useUpdateRFIMutation,
  useDeleteRFIMutation,
  // Workflow
  useSubmitRFIMutation,
  useAssignRFIMutation,
  useRespondToRFIMutation,
  useCloseRFIMutation,
  useVoidRFIMutation,
  // Comments
  useAddRFICommentMutation,
  useUpdateRFICommentMutation,
  useDeleteRFICommentMutation,
  // Distribution
  useAddRFIDistributionMutation,
  useRemoveRFIDistributionMutation,
  // References
  useAddRFIReferenceMutation,
  useRemoveRFIReferenceMutation,
  // Attachments
  useAddRFIAttachmentMutation,
  useRemoveRFIAttachmentMutation,
  // Statistics & Special
  useGetRFIStatsQuery,
  useGetMyRFIsQuery,
  useBulkAssignRFIsMutation,
  useBulkCloseRFIsMutation,
} = rfiApi;

/**
 * Utility functions for PDF operations
 */
export const getRFIPdfUrl = (projectId: string, rfiId: string): string => {
  return `/api/projects/${projectId}/rfis/${rfiId}/pdf`;
};

export const downloadRFIPdf = async (
  projectId: string,
  rfiId: string,
  rfiNumber: string
): Promise<void> => {
  const url = getRFIPdfUrl(projectId, rfiId);
  const token = localStorage.getItem('auth_token');

  const response = await fetch(url, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error('Failed to download PDF');
  }

  const blob = await response.blob();
  const downloadUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = downloadUrl;
  link.download = `${rfiNumber}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(downloadUrl);
};

/**
 * Re-export types for convenience
 */
export type { RFIListParams, RFIStats } from '../types/rfi';
